import React, { useContext } from 'react';
import styled from 'styled-components';
import { centered, useSharedElement } from '@/components/use-shared-element';
import { InputModal } from '@/components/input-modal';
import {
  SharedElementOverlay,
  SharedElementOverlayProps,
} from '@/components/shared-element-overlay';
import { Experience } from '@/webapi/use-experience-api';
import { ConfirmModal } from '@/components/confirm-modal';
import { MyExperiencesContext } from '@/features/dashboard/experiences/context';
import { useCanPublish } from '@/features/accounts/permissions';
import { VSpace } from '@/components/spacing';

export function useOptionsModals({ experience }: { experience: Experience }) {
  const {
    props: cloneModalProps,
    show: showCloneModal,
    hide: hideCloneModal,
    fromRef: cloneRef,
  } = useSharedElement(
    {
      showBackdrop: true,
      extraFrom: {
        background: `#dedede`,
        opacity: `0`,
      },
      extraTo: {
        background: `white`,
        opacity: `1`,
      },
    },
    undefined,
    () => centered(23, 42),
  );

  const {
    props: archiveModalProps,
    show: showArchiveModal,
    hide: hideArchiveModal,
    fromRef: archiveRef,
  } = useSharedElement(
    {
      showBackdrop: true,
      extraFrom: {
        background: `#dedede`,
        opacity: `0`,
      },
      extraTo: {
        background: `white`,
        opacity: `1`,
      },
    },
    undefined,
    () => centered(19, 42),
  );

  const {
    props: publishModalProps,
    show: showPublishModal,
    hide: hidePublishModal,
    fromRef: publishRef,
  } = useSharedElement(
    {
      showBackdrop: true,
      extraFrom: {
        background: `#dedede`,
        opacity: `0`,
      },
      extraTo: {
        background: `white`,
        opacity: `1`,
      },
    },
    undefined,
    () => centered(experience.isThemeTest ? 23 : 21, 42),
  );

  const {
    props: pauseModalProps,
    show: showPauseModal,
    hide: hidePauseModal,
    fromRef: pauseRef,
  } = useSharedElement(
    {
      showBackdrop: true,
      extraFrom: {
        background: `#dedede`,
        opacity: `0`,
      },
      extraTo: {
        background: `white`,
        opacity: `1`,
      },
    },
    undefined,
    () => centered(21, 42),
  );

  const {
    props: deleteModalProps,
    show: showDeleteModal,
    hide: hideDeleteModal,
    fromRef: deleteRef,
  } = useSharedElement(
    {
      showBackdrop: true,
      extraFrom: {
        background: `#dedede`,
        opacity: `0`,
      },
      extraTo: {
        background: `white`,
        opacity: `1`,
      },
    },
    undefined,
    () => centered(19, 42),
  );

  return {
    clone: {
      CloneModal,
      cloneModalProps: { hide: hideCloneModal, experience, ...cloneModalProps },
      showCloneModal,
      cloneRef,
    },
    archive: {
      ArchiveModal,
      archiveModalProps: {
        hide: hideArchiveModal,
        experience,
        ...archiveModalProps,
      },
      showArchiveModal,
      archiveRef,
    },
    publish: {
      PublishModal,
      publishModalProps: {
        hide: hidePublishModal,
        experience,
        ...publishModalProps,
      },
      showPublishModal,
      publishRef,
    },
    pause: {
      PauseModal,
      pauseModalProps: {
        hide: hidePauseModal,
        experience,
        ...pauseModalProps,
      },
      showPauseModal,
      pauseRef,
    },
    delete: {
      DeleteModal,
      deleteModalProps: {
        hide: hideDeleteModal,
        experience,
        ...deleteModalProps,
      },
      showDeleteModal,
      deleteRef,
    },
  };
}

export interface TooltipModalProps extends SharedElementOverlayProps {
  experience: Experience;
  hide: () => void;
}

function CloneModal(props: TooltipModalProps) {
  const { experience, hide, ...rest } = props;
  const { duplicate } = useContext(MyExperiencesContext);

  const onConfirm = async (name: string) => {
    await duplicate(experience, name);
    hide();
  };

  return (
    <SharedElementOverlay {...rest}>
      <InputModal
        title="Duplicate experience?"
        description="Name your experience duplicated experience."
        yesCaption="Duplicate"
        noCaption="Discard"
        initialValue={`Copy of ${experience?.name}`}
        onConfirm={onConfirm}
        onDiscard={hide}
      />
    </SharedElementOverlay>
  );
}

function ArchiveModal(props: TooltipModalProps) {
  const { experience, hide, ...rest } = props;
  const { archive } = useContext(MyExperiencesContext);

  const cantArchive = !useCanPublish();

  const onConfirm = async () => {
    if (cantArchive) {
      hide();
    } else {
      await archive(experience);
      hide();
    }
  };

  let description = `Are you sure you want to archive this experience? after archiving you wouldn't be able to run it again.`;
  let yesCaption = `Yes, Archive`;
  if (cantArchive) {
    description = `Your user has insufficient privileges to archive a published experience`;
    yesCaption = `Dismiss`;
  }

  return (
    <SharedElementOverlay {...rest} heightMultiplier={1.6}>
      <ConfirmModal
        neutral
        titleH="11rm"
        onlyDismiss={cantArchive}
        title={
          <FlexColCen>
            <p>Archive Experience?</p>
            <ModalExpName>{experience.name}</ModalExpName>
            <VSpace value={2} />
          </FlexColCen>
        }
        description={description}
        yesCaption={yesCaption}
        noCaption="Dismiss"
        onConfirm={onConfirm}
        onDiscard={hide}
      />
    </SharedElementOverlay>
  );
}

function PublishModal(props: TooltipModalProps) {
  const { experience, hide, ...rest } = props;
  const { publish } = useContext(MyExperiencesContext);
  const canPublish = useCanPublish();
  const onConfirm = async () => {
    if (canPublish) {
      await publish(experience, true);
    }
    hide();
  };

  let description: any = `Are you sure you want to publish this experience? Once confirm your target audience will start to receive it immediately`;
  let yesCaption = `Yes, Publish`;
  if (!canPublish) {
    description = `Your user has insufficient privileges to publish an experience`;
    yesCaption = `Dismiss`;
  }
  if (experience.isThemeTest) {
    description = (
      <span>
        Once you stop the experience, a process to unassign visitors from a
        specific theme will begin and be completed within two days.{` `}
        <strong>
          During this time, avoid removing the Visually script from the tested
          themes.
        </strong>
      </span>
    );
  }
  return (
    <SharedElementOverlay {...rest}>
      <ConfirmModal
        neutral
        onlyDismiss={!canPublish}
        title="Publish Experience?"
        description={description}
        yesCaption={yesCaption}
        noCaption="Dismiss"
        onConfirm={onConfirm}
        onDiscard={hide}
      />
    </SharedElementOverlay>
  );
}

function PauseModal(props: TooltipModalProps) {
  const { experience, hide, ...rest } = props;
  const { unpublish } = useContext(MyExperiencesContext);
  const canPublish = useCanPublish();
  const onConfirm = async () => {
    if (canPublish) {
      await unpublish(experience);
    }
    hide();
  };

  let description = (
    <>
      <span>
        Are you sure you want to pause this experience? Once confirm your target
        audience will stop receiving this experience.
      </span>
      <br />
      <span>
        <strong>
          Paused experiences will be archived after 30 days of inactivity.
        </strong>
      </span>
    </>
  );
  let yesCaption = `Yes, Pause`;
  if (!canPublish) {
    description = (
      <span>Your user has insufficient privileges to pause an experience</span>
    );
    yesCaption = `Dismiss`;
  }

  return (
    <SharedElementOverlay {...rest} heightMultiplier={1.6}>
      <ConfirmModal
        neutral
        titleH="11rem"
        onlyDismiss={!canPublish}
        title={
          <FlexColCen>
            <p>Pause Experience?</p>
            <ModalExpName>{experience.name}</ModalExpName>
            <VSpace value={2} />
          </FlexColCen>
        }
        description={description}
        yesCaption={yesCaption}
        noCaption="Dismiss"
        onConfirm={onConfirm}
        onDiscard={hide}
      />
    </SharedElementOverlay>
  );
}

function DeleteModal(props: TooltipModalProps) {
  const { experience, hide, ...rest } = props;
  const { remove } = useContext(MyExperiencesContext);

  const onConfirm = async () => {
    await remove(experience);
    hide();
  };

  return (
    <SharedElementOverlay {...rest} heightMultiplier={1.6}>
      <ConfirmModal
        titleH="11rem"
        neutral
        title={
          <FlexColCen>
            <p>Delete Experience?</p>
            <ModalExpName>{experience.name}</ModalExpName>
            <VSpace value={2} />
          </FlexColCen>
        }
        description="Are you sure you want to delete this experience? once delete its gone forever."
        yesCaption="Yes, Delete"
        noCaption="Dismiss"
        onConfirm={onConfirm}
        onDiscard={hide}
      />
    </SharedElementOverlay>
  );
}
const ModalExpName = styled.p`
  text-align: center;
  padding: 1rem 2rem;
  width: 364px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  border-radius: 8px;
  background-color: #f5f5f5;
  margin: 0;
  color: #2b2b2b;
  font-weight: normal;
  font-size: 1.62rem;
`;
const FlexColCen = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
